import { useContext, useMemo } from 'react';
import { ConfiguratorContext } from '@/components/Configurator/ConfiguratorContext';
import { useDisplayContext, useTranslator } from '@canvas-logic/clp-shared-ui';
import { labelId, type PtStep } from '@canvas-logic/clp-localization';

export interface AttributeTranslator {
  /** Translate the attribute display name (e.g. `bpt.shoe.attr.color.name`). */
  name(systemValue: string): string;
  /**
   * Translate a single attribute value label.
   *
   * `valueKey` must be the raw attribute value (string/RAL code) that the
   * server-side label registry uses as the value key in
   * `{step}.{ptKey}.attr.{attrKey}.val.{valueKey}`. `systemValue` is the
   * catalog-provided display label and is used as the fallback when the
   * project has no translation — preserves existing UX (catalog labels) for
   * attributes that haven't been translated yet.
   */
  value(valueKey: string | number | boolean | null | undefined, systemValue: string): string;
}

const identity: AttributeTranslator = {
  name: (systemValue) => systemValue,
  value: (_v, systemValue) => systemValue,
};

type ProductTypeResolver = {
  getComponentProductType?: (componentKey: string) => { key: string; isComposite: boolean } | undefined;
};

/**
 * Returns translators for the attribute identified by `componentKey + attributeKey`.
 *
 * The slot's filler PT type (basic vs composite) is read from the SDK so the
 * v5 label-ID prefix (`bpt`/`cpt`) is correct. Falls back to system values for
 * older baked assets that don't expose the discriminator.
 */
export function useAttributeTranslation(componentKey: string, attributeKey: string): AttributeTranslator {
  const ctx = useContext(ConfiguratorContext);
  const api = ctx?.sdk?.api as ProductTypeResolver | undefined;
  const pt = api?.getComponentProductType?.(componentKey);
  const translator = useTranslator();
  const displayCtx = useDisplayContext();

  return useMemo<AttributeTranslator>(() => {
    if (!pt) return identity;
    const step: PtStep = pt.isComposite ? 'cpt' : 'bpt';
    return {
      name: (systemValue) => translator.entity(labelId.attributeName(step, pt.key, attributeKey), displayCtx, systemValue),
      value: (valueKey, systemValue) => {
        if (valueKey === null || valueKey === undefined || valueKey === '') return systemValue;
        return translator.entity(labelId.attributeValue(step, pt.key, attributeKey, String(valueKey)), displayCtx, systemValue);
      },
    };
  }, [pt?.key, pt?.isComposite, attributeKey, translator, displayCtx]);
}
